import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useSelfServePhoneVerificationRollout } from 'src/hooks/use-self-serve-phone-verification-rollout';
import { usePhoneVerificationNudgeAnalytics } from 'src/hooks/use-phone-verification-nudge-analytics';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import {
  deriveNudgeLeg,
  parseSendStatus,
  parseStatusValue,
} from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import type { OtpHostContext, OtpHostOutcome } from '../phone-verification-cta/otp-host';

/**
 * FINTECH-4845. Data/gating hook for the NET-NEW, self-serve "Verify" button rendered next to
 * the Mobile Phone field on My Account -- deliberately separate from `usePhoneVerificationCta`
 * (the pre-existing "Verify Now" banner's own hook), which is NOT touched by this feature at
 * all. Per the human design decision: the two entry points must stay fully independent, each
 * behind its own gate, rather than sharing a composed gate. Some duplication with the banner
 * hook's shape is the deliberate cost of that isolation.
 *
 * Gate is the self-serve rollout flag ALONE -- no OR with the legacy nudge killswitch. Default
 * OFF (rollout dial), so this hook is a true no-op (no network call, isVisible always false)
 * until a dispensary/enterprise is explicitly targeted.
 */
export type SelfServeCtaState =
  | {
      kind: 'verified_other';
      dispensaryId?: string | null;
      enterpriseId?: string | null;
      ownerEmailHint?: string | null;
    }
  | { kind: 'leg_one'; dispensaryId?: string | null; enterpriseId?: string | null };

export type SelfServeBannerErrorKind = 'invalid_number' | 'rate_limited' | 'unavailable';

export type SelfServeBannerError = {
  kind: SelfServeBannerErrorKind;
};

export type UseSelfServeVerifyButtonResult = {
  isVisible: boolean;
  isSending: boolean;
  cta: SelfServeCtaState | null;
  bannerError: SelfServeBannerError | null;
  onVerifyClick: () => Promise<void>;
};

type SendPayload =
  | {
      status?: string | null;
      phoneHint?: string | null;
      errorCode?: string | null;
    }
  | null
  | undefined;

function mapSendFailure(payload: SendPayload): SelfServeBannerError {
  if (payload?.errorCode === 'rate_limited') {
    return { kind: 'rate_limited' };
  }
  if (payload?.errorCode === 'invalid_number' || payload?.errorCode === 'undeliverable') {
    return { kind: 'invalid_number' };
  }
  return { kind: 'unavailable' };
}

export function useSelfServeVerifyButton(): UseSelfServeVerifyButtonResult {
  const { openModal } = useModals();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.exists);
  const phone = useObserver(() => user.profile?.phone);
  const { dispensary } = useDispensary() as {
    dispensary?: { id?: string | null; retailer?: { enterpriseId?: string | null } | null };
  };
  const dispensaryId = dispensary?.id ?? null;
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? null;
  const { fetchStatus, sendCode } = usePhoneVerification();
  // The ONLY gate -- no legacy-killswitch OR. Default false everywhere until a
  // dispensary/enterprise is explicitly targeted; see the hook's own doc comment.
  const rolloutEnabled = useSelfServePhoneVerificationRollout();
  // Own analytics surface (`myAccountSelfServeButton`), never `myAccountBanner` -- see
  // FINTECH-4845 in events.ts for why this must stay a distinct funnel.
  const analytics = usePhoneVerificationNudgeAnalytics({ surface: 'myAccountSelfServeButton' });

  const [cta, setCta] = useState<SelfServeCtaState | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [bannerError, setBannerError] = useState<SelfServeBannerError | null>(null);
  const previousCtaKindRef = useRef<SelfServeCtaState['kind'] | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const requestIdRef = useRef(0);
  const conflictRecoveryRef = useRef(false);
  // Adversarial-review finding (addressed): unlike the always-mounted legacy banner, THIS
  // component can be unmounted mid-click by a live rollout-flag flip to off (my-account.jsx
  // suppresses it and remounts the banner instead) -- a race the banner structurally cannot
  // have. Guards the click continuation from resuming (opening the OTP modal, touching state)
  // after this specific unmount path, without pretending to fix the (separate, pre-existing,
  // inherited-from-the-banner) logout/navigation races this hook intentionally still shares.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isConflictRecovery = conflictRecoveryRef.current;
    let cancelled = false;

    if (!isLoggedIn || !rolloutEnabled) {
      // Logged-out or flag-off (its default everywhere): clear to hidden and issue no request
      // at all -- a true no-op. Must also clear bannerError (dutchie-reviewer finding,
      // addressed): isVisible is `cta !== null || bannerError !== null`, so a stale
      // bannerError from before a live flag flip to off would otherwise keep the button
      // visible despite the flag being off.
      setCta(null);
      setBannerError(null);
      previousCtaKindRef.current = null;
      conflictRecoveryRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    const run = async (): Promise<void> => {
      try {
        const { phoneVerificationStatus } = await fetchStatus(dispensaryId, enterpriseId);

        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        setBannerError(null);

        const leg = deriveNudgeLeg(phoneVerificationStatus?.status, phoneVerificationStatus?.hasPhone);
        const status = parseStatusValue(phoneVerificationStatus?.status);

        if (leg === 'leg_one') {
          setCta({ kind: 'leg_one', dispensaryId, enterpriseId });
          if (previousCtaKindRef.current !== 'leg_one') {
            analytics.track('shown', { leg: 'leg_one' });
          }
          previousCtaKindRef.current = 'leg_one';
        } else if (status === 'verified_other') {
          setCta({
            kind: 'verified_other',
            dispensaryId,
            enterpriseId,
            ownerEmailHint: phoneVerificationStatus?.ownerEmailHint ?? null,
          });
          if (previousCtaKindRef.current !== 'verified_other') {
            analytics.track('shown', { leg: 'leg_two' });
          }
          previousCtaKindRef.current = 'verified_other';
        } else {
          setCta(null);
          previousCtaKindRef.current = null;
        }
      } catch {
        if (!cancelled && requestId === requestIdRef.current) {
          if (isConflictRecovery) {
            setCta({ kind: 'verified_other', dispensaryId, enterpriseId, ownerEmailHint: null });
            if (previousCtaKindRef.current !== 'verified_other') {
              analytics.track('shown', { leg: 'leg_two' });
            }
            previousCtaKindRef.current = 'verified_other';
          } else {
            setCta(null);
            previousCtaKindRef.current = null;
          }
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          conflictRecoveryRef.current = false;
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, rolloutEnabled, dispensaryId, enterpriseId, phone, refreshToken]);

  const onVerifyClick = useCallback(async (): Promise<void> => {
    if (cta?.kind !== 'leg_one' || isSending) {
      return;
    }

    analytics.trackVerifyClicked();
    setIsSending(true);
    try {
      const result = await sendCode(cta.dispensaryId, cta.enterpriseId);
      if (!mountedRef.current) {
        // Unmounted (rollout flag flipped off) while the send was in flight -- the legacy
        // banner may already have taken this surface's place. Do not touch state or open a
        // modal for a component that's gone.
        return;
      }
      const payload = result.sendPhoneVerificationCode;

      if (parseSendStatus(payload?.status) !== 'sent') {
        const failure = mapSendFailure(payload);
        analytics.trackFailed(payload?.errorCode ?? undefined);
        setBannerError(failure);
        return;
      }

      analytics.trackCodeSent();
      setBannerError(null);

      const context: OtpHostContext = {
        phoneHint: payload?.phoneHint ?? null,
        dispensaryId: cta.dispensaryId,
        enterpriseId: cta.enterpriseId,
      };

      setIsSending(false);

      // Reuses the EXISTING OTP modal (same ModalNames.phoneVerificationOtp host the banner
      // opens) -- no new OTP flow.
      const outcome = (await openModal<OtpHostContext, OtpHostOutcome>(ModalNames.phoneVerificationOtp, context)) as
        | OtpHostOutcome
        | undefined;

      // False positive below: the lint rule can't see that mountedRef.current is mutated by the
      // unmount cleanup effect (an external ref write), asynchronously, between this read and
      // the one above.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!mountedRef.current) {
        // Same unmounted-mid-flight guard, now after the OTP round-trip itself.
        return;
      }

      if (outcome?.kind === 'verified') {
        analytics.trackCompleted();
        setCta(null);
        user.refetch();
        return;
      }

      if (outcome?.kind === 'conflict') {
        analytics.trackFailed('conflict');
        conflictRecoveryRef.current = true;
        setRefreshToken((token) => token + 1);
        return;
      }

      if (outcome?.kind === 'unavailable') {
        analytics.trackFailed();
        setBannerError({ kind: 'unavailable' });
        return;
      }

      analytics.trackDismissed();
    } catch {
      analytics.trackFailed();
      setBannerError({ kind: 'unavailable' });
    } finally {
      setIsSending(false);
    }
  }, [analytics, cta, isSending, sendCode, openModal, user]);

  return useMemo(
    () => ({ isVisible: cta !== null || bannerError !== null, isSending, cta, bannerError, onVerifyClick }),
    [cta, bannerError, isSending, onVerifyClick]
  );
}
